/**
 * Individual item row component for the Apple-style Items List.
 *
 * Extracted from ShopifyItemsTable.tsx to keep files under size limits.
 * Handles: normal item rows, addon rows, bundle sub-items, personalization cards.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Minus, Trash2, MoreHorizontal } from 'lucide-react';
import { BundleBadge } from '@/features/selling/sales-order-detail/components/BundleBadge';
import { OrderItemBundleSection } from '@/features/selling/sales-order-detail/components/OrderItemBundleSection';
import { OrderItemPersonalizationCard } from '@/features/selling/sales-order-detail/components/OrderItemPersonalizationCard';

/**
 * Type for item row data
 */
export type ItemRow = {
  name?: string;
  idx?: number;
  item_code?: string;
  item_name?: string;
  image?: string;
  item_image?: string;
  thumbnail?: string;
  description?: string;
  item_description?: string;
  qty?: number;
  quantity?: number;
  rate?: number;
  price?: number;
  amount?: number;
  total?: number;
  price_list_rate?: number;
  discount_amount?: number;
  uom?: string;
  stock_uom?: string;
  conversion_factor?: number;
  customization_parent_row?: string;
  __islocal?: 0 | 1;
  __unsaved?: 0 | 1;
};

/**
 * Format currency value
 */
export function formatCurrency(value: any, currency: string = 'THB'): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';

  const symbols: Record<string, string> = {
    'THB': '฿',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
  };

  const symbol = symbols[currency] || currency;
  return `${symbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format number
 */
export function formatNumber(value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Render product info with thumbnail, name, and code (Apple-style)
 */
function renderProductInfo(
  row: ItemRow,
  getFieldValue: (row: any, fieldname: string) => any,
  toAbsoluteUrl: (path?: string | null) => string | null,
  itemRank?: number,
  skuLabel?: string,
  isBundle?: boolean
): React.ReactNode {
  const productName = getFieldValue(row, 'item_name') ||
    getFieldValue(row, 'description') ||
    getFieldValue(row, 'item_description') ||
    null;

  const sku = getFieldValue(row, 'item_code');

  const rawImageUrl = getFieldValue(row, 'image') ||
    getFieldValue(row, 'item_image') ||
    getFieldValue(row, 'thumbnail');

  const imageUrl = toAbsoluteUrl(rawImageUrl);
  const displayName = productName || (sku ? `Item ${sku}` : "Unnamed item");
  const isTopItem = itemRank !== undefined && itemRank <= 3;

  // Initials fallback when no image
  const initials = (() => {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  })();
  const initialsHue = (() => {
    let hash = 0;
    for (let i = 0; i < displayName.length; i++) hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
    return Math.abs(hash) % 360;
  })();

  return (
    <div className="flex items-center gap-4 min-w-0 flex-1">
      <div className="w-[72px] h-[72px] rounded-sm bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0 border border-gray-200 dark:border-gray-700 shadow-sm">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={displayName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-base font-semibold select-none"
            style={{ backgroundColor: `hsl(${initialsHue}, 45%, 88%)`, color: `hsl(${initialsHue}, 50%, 35%)` }}
          >
            {initials}
          </div>
        )}
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {productName ? (
            <span className="text-sm font-normal text-gray-900 dark:text-gray-100 leading-snug">
              {productName}
            </span>
          ) : (
            <span className="text-sm font-normal text-gray-400 dark:text-gray-500 italic leading-snug">
              {sku ? `Item ${sku}` : "Unnamed item"}
            </span>
          )}
          {isTopItem && (
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] px-2 py-0.5 h-5 font-medium",
                itemRank === 1
                  ? "border-primary/30 text-primary"
                  : "border-border/40 text-muted-foreground"
              )}
            >
              #{itemRank}
            </Badge>
          )}
          {isBundle && <BundleBadge />}
        </div>
        {sku && (
          <span className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            {skuLabel || 'SKU'}: {sku}
          </span>
        )}
      </div>
    </div>
  );
}

export interface ShopifyItemRowProps {
  row: ItemRow;
  index: number;
  items: ItemRow[];
  isFirst: boolean;
  isLast: boolean;
  currency: string;
  isNew: boolean;
  disabled: boolean;
  getFieldValue: (row: any, fieldname: string) => any;
  toAbsoluteUrl: (path?: string | null) => string | null;
  resolvedLabels: { itemCode: string; qty: string; amount: string; discountAmount: string };
  uiLabels: Record<string, string>;
  editingRates: Map<number, string>;
  onEditingRateChange: (index: number, value: string | undefined) => void;
  onQuantityChange: (index: number, qty: number) => void;
  onRateChange: (index: number, rate: number) => void;
  onOpenDetails: (index: number, e: React.MouseEvent) => void;
  onRemoveItem: (index: number, e: React.MouseEvent) => void;
  itemRank: number;
  bundleData?: { isBundle: boolean; packedItems: any[]; customizations: any[] };
}

export function ShopifyItemRow({
  row,
  index,
  items,
  isFirst,
  isLast,
  currency,
  isNew,
  disabled,
  getFieldValue,
  toAbsoluteUrl,
  resolvedLabels,
  uiLabels,
  editingRates,
  onEditingRateChange,
  onQuantityChange,
  onRateChange,
  onOpenDetails,
  onRemoveItem,
  itemRank,
  bundleData,
}: ShopifyItemRowProps) {
  const qty = getFieldValue(row, 'qty') || getFieldValue(row, 'quantity') || 0;
  const rate = getFieldValue(row, 'rate') || getFieldValue(row, 'price') || 0;
  const priceListRate = getFieldValue(row, 'price_list_rate') || 0;
  const amount = getFieldValue(row, 'amount') || getFieldValue(row, 'total') || 0;

  // Addon row detection — personalization service items linked to a parent
  const parentRowName = row.customization_parent_row;
  if (parentRowName) {
    const parentRow = items.find((r) => r.name === parentRowName);
    const parentName = parentRow
      ? (getFieldValue(parentRow, 'item_name') || getFieldValue(parentRow, 'item_code') || '')
      : '';
    return (
      <React.Fragment>
        <div className="mx-6 sm:mx-8 border-t border-dashed border-border/30" />
        <div className="flex items-center justify-between px-6 sm:px-8 py-3 pl-12 sm:pl-14">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-sm text-muted-foreground">
              ↳ {getFieldValue(row, 'item_name') || 'Personalization Service'}
            </span>
            {parentName && (
              <span className="text-xs text-muted-foreground/70">
                addon for {parentName}
              </span>
            )}
          </div>
          <span className="text-sm font-medium text-muted-foreground flex-shrink-0 ml-4">
            {formatCurrency(Number(amount), currency)}
          </span>
        </div>
        {!isLast && (
          <div className="mx-6 sm:mx-8 border-t border-border/20" />
        )}
      </React.Fragment>
    );
  }

  // Normal item row
  const hasDiscount = priceListRate > 0 && Number(priceListRate) > Number(rate);
  const originalPrice = priceListRate > 0 ? Number(priceListRate) : Number(rate);
  const originalTotal = originalPrice * Number(qty);
  const discountedTotal = Number(amount) || 0;
  const totalDiscountAmount = hasDiscount ? (originalTotal - discountedTotal) : 0;
  const totalDiscountPercentage = hasDiscount && originalTotal > 0
    ? ((totalDiscountAmount / originalTotal) * 100).toFixed(1)
    : "0.0";

  return (
    <React.Fragment>
      <div
        className={cn(
          "flex flex-col md:flex-row md:items-center justify-between px-6 sm:px-8 transition-all duration-150",
          "min-h-[76px] md:min-h-0",
          isFirst ? "pt-6 sm:pt-7 pb-5" : isLast ? "py-6 sm:py-7 pb-6 sm:pb-7" : "py-6 sm:py-7",
        )}
      >
        {/* Product Info - Left Side */}
        <div className="flex-1 min-w-0">
          {renderProductInfo(row, getFieldValue, toAbsoluteUrl, itemRank, resolvedLabels.itemCode, bundleData?.isBundle)}
        </div>

        {/* Right Side: Quantity, Rate, Total, and Actions */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 flex-shrink-0 md:ml-8 mt-4 md:mt-0">
          {/* Quantity Stepper */}
          <div className="flex flex-col items-end md:items-center space-y-1">
            <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              {resolvedLabels.qty}
            </span>
            {isNew && !disabled ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange(index, Math.max(0, Number(qty) - 1));
                  }}
                  disabled={Number(qty) <= 0}
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <Input
                  type="number"
                  value={qty}
                  onChange={(e) => {
                    e.stopPropagation();
                    const newQty = parseFloat(e.target.value) || 0;
                    onQuantityChange(index, newQty);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-16 h-8 text-center text-base font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  min={0}
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange(index, Number(qty) + 1);
                  }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <span className="text-base font-medium text-gray-900 dark:text-gray-100 md:min-w-[3rem] md:text-center">
                {formatNumber(qty)}
              </span>
            )}
          </div>

          {/* Rate Input */}
          {isNew && !disabled && (
            <div className="flex flex-col items-end md:items-center space-y-1">
              <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                {uiLabels.rate}
              </span>
              <Input
                type="number"
                value={editingRates.has(index) ? editingRates.get(index) : rate}
                onChange={(e) => {
                  e.stopPropagation();
                  onEditingRateChange(index, e.target.value);
                }}
                onBlur={(e) => {
                  const newRate = parseFloat(e.target.value) || 0;
                  onEditingRateChange(index, undefined);
                  onRateChange(index, newRate);
                }}
                onFocus={() => {
                  onEditingRateChange(index, String(rate));
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-24 h-8 text-right text-base font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                min={0}
                step={0.01}
              />
            </div>
          )}

          {/* Total */}
          <div className="flex flex-col items-end space-y-1 md:min-w-[8rem]">
            <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              {resolvedLabels.amount}
            </span>
            {hasDiscount ? (
              <div className="flex flex-col items-end space-y-1">
                <span className="text-xs text-gray-500 dark:text-gray-400 line-through">
                  {formatCurrency(originalTotal, currency)}
                </span>
                <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(discountedTotal, currency)}
                </span>
                <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                  -{formatCurrency(totalDiscountAmount, currency)} ({totalDiscountPercentage}%)
                </span>
              </div>
            ) : (
              <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(discountedTotal, currency)}
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={(e) => onOpenDetails(index, e)}
              title={uiLabels.moreDetails}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {isNew && !disabled && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={(e) => onRemoveItem(index, e)}
                title={uiLabels.removeItem}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Bundle sub-items section */}
      {bundleData?.isBundle && bundleData.packedItems.length > 0 && (
        <div className="px-6 sm:px-8 pb-4">
          <OrderItemBundleSection packedItems={bundleData.packedItems} />
        </div>
      )}

      {/* Personalization card */}
      {bundleData?.customizations && bundleData.customizations.length > 0 && (
        <div className="px-6 sm:px-8 pb-4">
          <OrderItemPersonalizationCard
            customizations={bundleData.customizations}
            packedItems={bundleData.packedItems}
          />
        </div>
      )}

      {/* Divider */}
      {!isLast && (
        <div className="mx-6 sm:mx-8 border-t border-border/20" />
      )}
    </React.Fragment>
  );
}
